import { Dimension } from "@models/IDimension";
import { AssignmentFilterInput, IdRangeInput } from "@models/ISchema";
import { IAssignedBlocks } from "@services/types/advance-search/advanceSearchTypes";
import { Option } from "react-select/lib/filters";
import { create } from "zustand";

export interface AdvancedSearchLabel {
  id: string;
  code: string;
  name: string;
}

interface AdvancedSearchState {
  dimension: Dimension | null;
  properties: {
    campus: Option | null;
    building: Option | null;
    department: Option | null;
    modality: Option | null;
    shift: Option | null;
    program: Option | null;
    curriculum: Option | null;
    level: Option | null;
    course: Option | null;
  };
  classroomType: Option | null;
  objCapacity?: {
    capacity: {
      value: {
        gte: string | null;
        lte: string | null;
      };
    };
  };
  objAssignedBlocks?: IAssignedBlocks;
  days: (number | string)[];
  blockRange?: {
    start?: string;
    end?: string;
  };
  daysError: boolean;
  error: boolean;
  timeRangeError: boolean;
  intervalRange?: IdRangeInput | null;
  intervals?: number[] | null;
  contractType: Option | null;
  dummyInstructors: boolean;
  component: Option | null;
  objAssignment?: AssignmentFilterInput;
  labelCategory?: Option | null;
  selectedLabels: { [key: string]: AdvancedSearchLabel };
  hasState: boolean;
}

const initialState: AdvancedSearchState = {
  dimension: null,
  properties: {
    campus: null,
    building: null,
    department: null,
    modality: null,
    shift: null,
    program: null,
    curriculum: null,
    level: null,
    course: null,
  },
  classroomType: null,
  objCapacity: {} as any,
  objAssignedBlocks: {} as any,
  days: [],
  blockRange: {} as any,
  daysError: false,
  error: false,
  timeRangeError: false,
  intervalRange: null,
  intervals: [],
  contractType: null,
  dummyInstructors: false,
  component: null,
  objAssignment: {} as AssignmentFilterInput,
  labelCategory: null,
  selectedLabels: {} as { [key: string]: AdvancedSearchLabel },
  hasState: false,
};

export const useAdvancedSearchStore = create<AdvancedSearchState>(() =>
  structuredClone(initialState),
);

export const resetAdvancedSearchStore = () =>
  useAdvancedSearchStore.setState(structuredClone(initialState));

export const setAdvancedSearchDimension = (dimension: Dimension) => {
  const currentState = useAdvancedSearchStore.getState();

  if (currentState.dimension === dimension) {
    return;
  }

  useAdvancedSearchStore.setState({
    ...structuredClone(initialState),
    dimension,
    hasState: true,
  });
};

export const setAdvancedSearchProperty = (
  property: keyof AdvancedSearchState["properties"],
  value: Option | null,
) => {
  const currentState = useAdvancedSearchStore.getState();

  useAdvancedSearchStore.setState({
    ...currentState,
    hasState: true,
    properties: {
      ...currentState.properties,
      [property]: value,
    },
  });
};

export const resetAdvancedSearchProperties = (
  properties: (keyof AdvancedSearchState["properties"])[],
) => {
  const currentState = useAdvancedSearchStore.getState();
  const propertiesToReset = properties.filter(property => currentState.properties[property]);

  useAdvancedSearchStore.setState({
    ...currentState,
    hasState: true,
    properties: {
      ...currentState.properties,
      ...propertiesToReset.reduce((acc, property) => ({ ...acc, [property]: null }), {}),
    },
  });
};

export const setAdvancedSearchClassroomType = (value: Option | null) => {
  useAdvancedSearchStore.setState({
    classroomType: value,
    hasState: true,
  });
};

export const setAdvancedSearchCapacity = (value?: any) => {
  useAdvancedSearchStore.setState({
    objCapacity: value,
    hasState: true,
  });
};

export const setAdvancedSearchDays = (value?: (number | string)[]) => {
  useAdvancedSearchStore.setState({
    days: value,
    hasState: true,
  });
};

export const setAdvancedSearchBlockRange = (value?: { start?: string; end?: string }) => {
  useAdvancedSearchStore.setState({
    blockRange: value,
    hasState: true,
  });
};

export const setAdvancedSearchDaysError = (value: boolean) => {
  useAdvancedSearchStore.setState({
    daysError: value,
    hasState: true,
  });
};

export const setAdvancedSearchError = (value: boolean) => {
  useAdvancedSearchStore.setState({
    error: value,
    hasState: true,
  });
};

export const setAdvancedSearchTimeRangeError = (value: boolean) => {
  useAdvancedSearchStore.setState({
    timeRangeError: value,
    hasState: true,
  });
};

export const setAdvancedSearchIntervalRange = (value?: IdRangeInput | null) => {
  useAdvancedSearchStore.setState({
    intervalRange: value,
    hasState: true,
  });
};

export const setAdvancedSearchIntervals = (value?: number[] | null) => {
  useAdvancedSearchStore.setState({
    intervals: value,
    hasState: true,
  });
};

export const setAdvancedSearchContractType = (value: Option | null) => {
  useAdvancedSearchStore.setState({
    contractType: value,
    hasState: true,
  });
};

export const setAdvancedSearchDummyInstructors = (value: boolean) => {
  useAdvancedSearchStore.setState({
    dummyInstructors: value,
    hasState: true,
  });
};

export const setAdvancedSearchAssignedBlocks = (value?: any) => {
  useAdvancedSearchStore.setState({
    objAssignedBlocks: value,
    hasState: true,
  });
};

export const setAdvancedSearchComponent = (value: Option | null) => {
  useAdvancedSearchStore.setState({
    component: value,
    hasState: true,
  });
};

export const setAdvancedSearchAssignment = (value?: AssignmentFilterInput) => {
  useAdvancedSearchStore.setState({
    objAssignment: value,
    hasState: true,
  });
};

export const setAdvancedSearchLabelCategory = (value?: Option | null) => {
  useAdvancedSearchStore.setState({
    labelCategory: value,
    hasState: true,
  });
};

export const setAdvancedSearchSelectedLabels = (value?: { [key: string]: AdvancedSearchLabel }) => {
  useAdvancedSearchStore.setState({
    selectedLabels: value,
    hasState: true,
  });
};
